import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["pressed", "defaultPressed", "onClick"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useState } from 'react';
import { BUTTON_SIZES, BUTTON_TYPES } from '../../constants';
import { Button } from '../Button/Button';

/**
 * Toggle buttons are an official
 * [WAI-ARIA button](https://www.w3.org/TR/wai-aria-practices/#button) type that use the
 * [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) attribute to tell assistive
 * technologies that the button is a toggle button.
 *
 * **Important:** it is critical the label on a toggle does not change when its state changes.
 *
 * @extends Button
 * @since 8.0.0
 */
export var ToggleButton = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var pressed = _ref.pressed,
    defaultPressed = _ref.defaultPressed,
    onClick = _ref.onClick,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(defaultPressed),
    _useState2 = _slicedToArray(_useState, 2),
    pressedState = _useState2[0],
    setPressedState = _useState2[1];
  var onClickCallback = useCallback(function (e) {
    setPressedState(!pressedState);
    if (onClick) {
      onClick(e, typeof pressed !== 'undefined' ? pressed : !pressedState);
    }
  }, [onClick, pressed, pressedState]);

  // If pressed is defined, use that value as a controlled value, otherwise use local
  // uncontrolled state.
  var ariaPressed = pressedState;
  if (typeof pressed !== 'undefined') {
    ariaPressed = pressed;
  }
  return /*#__PURE__*/React.createElement(Button, _extends({
    "aria-pressed": !!ariaPressed,
    onClick: onClickCallback,
    ref: forwardedRef
  }, rest));
});
ToggleButton.displayName = 'ToggleButton';
ToggleButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Change the type of the toggle button. All button types support a pressed state, but it is
   * recommended that you use the default "tertiary" type when using toggle buttons.
   */
  buttonType: PropTypes.oneOf(Object.values(BUTTON_TYPES)),
  /**
   * Sets the [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) state and use as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * A toggle button can use `pressed` or `defaultPressed`, but not both.
   */
  defaultPressed: PropTypes.bool,
  /**
   * Set the button as disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Add an `Icon` to the button.
   */
  icon: PropTypes.node,
  /**
   * A click handler that will be called with two arguments, `e`, the button click event,
   * and `pressed`, the current pressed state.
   */
  onClick: PropTypes.func,
  /**
   * Sets the [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) state and use as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * A toggle button can use `pressed` or `defaultPressed`, but not both.
   */
  pressed: PropTypes.bool,
  /**
   * Change the button size. It is recommended that you use the default "sm" size when using
   * toggle buttons.
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES))
} : {};
ToggleButton.defaultProps = {
  buttonType: BUTTON_TYPES.tertiary,
  defaultPressed: undefined,
  disabled: false,
  icon: null,
  onClick: undefined,
  pressed: undefined,
  size: BUTTON_SIZES.sm
};